import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
} from 'antd';
import {
  EnvironmentOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { businessAddressSettingsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const IRAN_COUNTRY_ID = 101;

const BusinessAddresses = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [defaultLoadingId, setDefaultLoadingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [countries, setCountries] = useState([]);
  const [form] = Form.useForm();

  const countryOptions = useMemo(
    () => (countries || []).map((c) => ({ value: c.id, label: c.nicename || c.name })),
    [countries]
  );

  const fetchIndex = async () => {
    setLoading(true);
    try {
      const res = await businessAddressSettingsApi.getAll();
      const data = res?.company_addresses || res?.data?.company_addresses || [];
      setRows(data);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت نشانی‌ها');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndex();
  }, []);

  const openCreate = async () => {
    setEditingId(null);
    setModalOpen(true);
    setModalLoading(true);
    form.resetFields();
    try {
      const res = await businessAddressSettingsApi.getCreateData();
      const list = res?.data?.countries || [];
      setCountries(list);
      form.setFieldsValue({ country: IRAN_COUNTRY_ID });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات فرم');
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  const openEdit = async (id) => {
    setEditingId(id);
    setModalOpen(true);
    setModalLoading(true);
    form.resetFields();
    try {
      const res = await businessAddressSettingsApi.getEditData(id);
      const data = res?.data || {};
      setCountries(data?.countries || []);
      const a = data?.company_address || {};
      form.setFieldsValue({
        country: a.country_id || IRAN_COUNTRY_ID,
        address: a.address || '',
        location: a.location || '',
        tax_number: a.tax_number || '',
        tax_name: a.tax_name || '',
        latitude: a.latitude || '',
        longitude: a.longitude || '',
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات ویرایش');
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        country: values.country,
        address: values.address,
        location: values.location,
        tax_number: values.tax_number || null,
        tax_name: values.tax_name || null,
        latitude: values.latitude || null,
        longitude: values.longitude || null,
      };
      setModalSubmitting(true);
      if (editingId) {
        await businessAddressSettingsApi.update(editingId, payload);
        message.success('نشانی بروزرسانی شد');
      } else {
        await businessAddressSettingsApi.create(payload);
        message.success('نشانی جدید ثبت شد');
      }
      setModalOpen(false);
      fetchIndex();
    } catch (error) {
      if (error?.errorFields) return;
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره نشانی');
    } finally {
      setModalSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await businessAddressSettingsApi.delete(id);
      message.success('نشانی حذف شد');
      fetchIndex();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف نشانی');
    }
  };

  const onSetDefault = async (id) => {
    setDefaultLoadingId(id);
    try {
      await businessAddressSettingsApi.setDefault(id);
      message.success('نشانی پیش‌فرض تنظیم شد');
      fetchIndex();
    } catch (error) {
      message.error(error?.message || 'خطا در تنظیم نشانی پیش‌فرض');
    } finally {
      setDefaultLoadingId(null);
    }
  };

  const columns = [
    {
      title: 'موقعیت',
      dataIndex: 'location',
      key: 'location',
      render: (v, row) => (
        <Space>
          <span>{v || '—'}</span>
          {Number(row?.is_default) === 1 ? <Tag color="green">پیش‌فرض</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'کشور',
      key: 'country',
      render: (_, row) => row?.country?.nicename || row?.country?.name || '—',
    },
    {
      title: 'نشانی',
      dataIndex: 'address',
      key: 'address',
      render: (v) => <span className="line-clamp-2">{v || '—'}</span>,
    },
    // {
    //   title: 'مختصات',
    //   key: 'geo',
    //   render: (_, row) =>
    //     row?.latitude && row?.longitude ? `${row.latitude}, ${row.longitude}` : '—',
    // },
    {
      title: 'عملیات',
      key: 'actions',
      width: 220,
      render: (_, row) => (
        <Space size="small" wrap>
          {Number(row?.is_default) !== 1 ? (
            <Button
              type="default"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => onSetDefault(row.id)}
              loading={defaultLoadingId === row.id}
              disabled={defaultLoadingId != null}
            >
              پیش‌فرض
            </Button>
          ) : null}
          <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row.id)} />
          <Popconfirm
            title="حذف این نشانی؟"
            okText="حذف"
            cancelText="لغو"
            onConfirm={() => onDelete(row.id)}
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-700/70 flex items-center justify-center">
                <EnvironmentOutlined className="text-gray-700 dark:text-gray-300 text-xl" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">نشانی کسب‌وکار</h1>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchIndex} loading={loading}>
                بروزرسانی
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                className={PANEL_PRIMARY_BTN_CLASS}
                onClick={openCreate}
              >
                نشانی جدید
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 900 }}
              />
            </Card>
          </div>
        </Card>

        <Modal
          title={editingId ? 'ویرایش نشانی کسب‌وکار' : 'افزودن نشانی کسب‌وکار'}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={onSubmit}
          okText="ذخیره"
          cancelText="لغو"
          confirmLoading={modalSubmitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" className="custom-font" disabled={modalLoading}>
            <Form.Item name="country" label="کشور">
              <Select
                options={countryOptions}
                showSearch
                optionFilterProp="label"
                popupClassName="custom-font"
                loading={modalLoading}
              />
            </Form.Item>
            <Form.Item name="location" label="موقعیت (مثال: دفتر مرکزی)">
              <Input />
            </Form.Item>
            <Form.Item name="address" label="نشانی">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="tax_number" label="شماره مالیاتی">
              <Input />
            </Form.Item>
            <Form.Item name="tax_name" label="نام مالیاتی">
              <Input />
            </Form.Item>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Form.Item name="latitude" label="Latitude">
                <Input />
              </Form.Item>
              <Form.Item name="longitude" label="Longitude">
                <Input />
              </Form.Item>
            </div>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default BusinessAddresses;
